import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { ToastType } from '@components';
import { Button } from '@components/Button';
import { datadogRum } from '@datadog/browser-rum';
import { apiCallsStore } from '@store/api/store';
import { closeModal, isModalOpen } from '@store/modals';
import { CloseIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { createSignal } from 'solid-js';

type CreateJiraSupportTicketModel = {
  IssueText: string;
  RumSessionId: string;
  CurrentUrl: string;
  ApiCalls: string[];
};

const modalId = 'SupportModal';

export const SupportModal = () => {
  const [desc, setDesc] = createSignal<string>('');
  const [loading, setLoading] = createSignal<boolean>(false);

  const onSubmit = async () => {
    setLoading(true);
    try {
      const rumContext = datadogRum.getInternalContext();

      const requestObject: CreateJiraSupportTicketModel = {
        IssueText: desc().trim(),
        RumSessionId:
          rumContext?.session_id == null ? '' : rumContext.session_id,
        CurrentUrl: window.location.href,
        ApiCalls: apiCallsStore.apiCalls.map((a) => {
          return `Url: ${a.url}, Method: ${a.method}, StatusCode: ${a.status}, Time: ${a.timeStamp}`;
        }),
      };

      const result: V4ApiResponse<null> = await apiClient.post(
        'Support/SubmitSupportTicket',
        requestObject,
      );

      if (Boolean(result.isSuccess)) {
        handleToast(
          ToastType.Success,
          'Your support ticket was successfully submitted! Please contact ithelpdesk@armstrongtransport for additional support.',
          `Ticket # ${result.value}`,
        );
        setLoading(false);
        closeModal(modalId);
        setDesc('');
      } else {
        handleToast(
          ToastType.Error,
          'We were unable to submit your support ticket. Please try again, or contact ithelpdesk@armstrongtransport for assistance.',
        );
        setLoading(false);
      }
    } catch {
      handleToast(
        ToastType.Error,
        'We were unable to submit your support ticket. Please try again, or contact ithelpdesk@armstrongtransport for assistance.',
      );
      setLoading(false);
    }
  };

  const onCloseModal = () => {
    setDesc('');
    closeModal(modalId);
  };

  return (
    <Dialog
      id={modalId}
      title="Request Support"
      class="w-[25vw] min-w-[360px] rounded-[5px] bg-white"
      onClose={onCloseModal}
      open={isModalOpen(modalId)}
    >
      <DialogTitle class="flex flex-row">
        <div class="flex-1">Request Support</div>
        <div>
          <span
            class="cursor-pointer"
            onClick={() => {
              closeModal('SupportModal');
            }}
          >
            <CloseIcon size={20} color="rgba(0, 0, 0, 0.54)" />
          </span>
        </div>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Issue Description"
          dataTestId={'issue-description-textInput'}
          value={desc()}
          multiline
          rows={3}
          style={{
            resize: 'none',
          }}
          maxLength={1000}
          name="Issue Description"
          error={desc().length > 1000}
          disabled={loading()}
          onChange={(v: string) => {
            setDesc(v);
          }}
        />
      </DialogContent>
      <DialogActions class="mb-2 pr-2">
        <Button
          isLoading={loading()}
          variant="contained"
          label="Submit Support Ticket"
          type="button"
          disabled={!desc().trim()}
          onClick={onSubmit}
          dataTestId={formatDataTestId('Submit Support Ticket', 'button')}
        />
        <Button
          class="float-right"
          variant="text"
          label="Cancel"
          type="button"
          onClick={() => {
            closeModal('SupportModal');
          }}
          dataTestId={formatDataTestId('Cancel', 'button')}
        />
      </DialogActions>
    </Dialog>
  );
};
